/** Reusable status badge for session rows + dashboard cards.
 *
 * Color mapping mirrors the inspection-report PDF's pill palette:
 *   closed   → emerald (the success / complete state)
 *   open/recording → sky (the active / in-flight state)
 *   created  → slate (idle / waiting)
 *   expired  → amber (was scheduled, nobody showed up — soft fail)
 *
 * Translation keys live under `dashboard.sessionStatus.{status}` —
 * already present in every locale bundle.
 */
import { useTranslation } from "react-i18next";

import type { SessionStatus } from "@/lib/types";


export function StatusPill({ status }: { status: SessionStatus }) {
  const { t } = useTranslation();
  const styles: Record<SessionStatus, string> = {
    closed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    recording: "bg-sky-500/15 text-sky-300 border-sky-500/30",
    paused: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    open: "bg-sky-500/15 text-sky-300 border-sky-500/30",
    created: "bg-slate-700/40 text-nb-text border-slate-600/50",
    expired: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    cancelled: "bg-slate-700/40 text-nb-text-2 border-slate-600/50 line-through",
  };
  return (
    <span
      className={
        "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium " +
        styles[status]
      }
    >
      {t(`dashboard.sessionStatus.${status}`)}
    </span>
  );
}
